import React, { useState, useEffect } from 'react';
import { Receipt, ExternalLink, CheckCircle, XCircle, Clock } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiGet } from '../../lib/apiClient';
import { API_ROUTES } from '../../lib/apiRoutes';

export default function PaymentHistory() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchHistory(); }, []);

  // Show success toast if redirected from Cryptomus with ?success=1
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('success') === '1') {
      toast.success('Payment received! Subscription activating...');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const data = await apiGet(`${API_ROUTES.app.payments}/history`);
      setPayments(data.payments || []);
    } catch {
      toast.error('Failed to load payment history');
    } finally {
      setLoading(false);
    }
  };

  const statusIcon = (status) => {
    if (status === 'approved') return <CheckCircle size={16} className="text-green-400" />;
    if (status === 'rejected') return <XCircle size={16} className="text-red-400" />;
    return <Clock size={16} className="text-amber-400" />;
  };

  const statusBadge = (status) => {
    const map = {
      pending: 'bg-amber-500/20 text-amber-400',
      approved: 'bg-green-500/20 text-green-400',
      rejected: 'bg-red-500/20 text-red-400',
    };
    return map[status] || 'bg-gray-700 text-gray-400';
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-100">Payment History</h1>
        <p className="text-sm text-gray-500 mt-1">Your past and pending payments</p>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading...</div>
      ) : !payments.length ? (
        <div className="text-center py-16 bg-dark-card border border-dark-border rounded-xl">
          <Receipt size={40} className="mx-auto mb-3 text-gray-600" />
          <p className="text-gray-400 mb-1">No payments yet</p>
          <p className="text-gray-600 text-sm">Upgrade your plan to get started</p>
        </div>
      ) : (
        <div className="space-y-3">
          {payments.map((p) => (
            <div key={p.id} className="bg-dark-card border border-dark-border rounded-xl p-5 flex items-center justify-between">
              <div className="flex items-center gap-4">
                {statusIcon(p.status)}
                <div>
                  <p className="text-gray-200 font-medium capitalize">{p.planSlug} <span className="text-gray-500 font-normal">— {p.billingCycle}</span></p>
                  <p className="text-gray-500 text-xs mt-0.5">{p.createdAt?.split('T')[0]}</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="text-gray-200 font-semibold">${p.amountUsd}</p>
                  {p.originalAmount !== p.amountUsd && (
                    <p className="text-gray-600 text-xs line-through">${p.originalAmount}</p>
                  )}
                </div>
                <span className={`px-2.5 py-0.5 rounded text-xs font-medium ${statusBadge(p.status)}`}>{p.status}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
